import { ConnectionMode } from './connection-mode.enum';
import { IntegrationVendor } from './integration-vendor.enum';

/**
 * Vendor-specific setup field (rendered in the connect-modal form).
 */
export interface VendorSetupField {
    key: string;
    labelKey: string;
    helpTextKey?: string;
    type: 'text' | 'password' | 'url';
    required: boolean;
    defaultValue?: string;
    placeholder?: string;
}

/**
 * The slice of VendorDefinition the universal integration card actually
 * renders. Structurally satisfied by every VendorDefinition — and by ad-hoc
 * views for non-vendor cards (partner apps, outbound API credentials), which
 * share the exact same look (REV-21656).
 */
export interface IntegrationCardView {
    /** Stable identifier, used only for data-testids. */
    vendor: string;

    /**
     * User- or partner-supplied name, rendered VERBATIM. Never route such a
     * value through the i18next pipe as a key: i18next is not a passthrough —
     * a `:` is a namespace separator (swallows the prefix) and `$t(...)`
     * resolves foreign translation keys. Exactly one of raw/key is set.
     */
    displayNameRaw?: string;
    /** i18n key OR raw display text (i18next passes unknown keys through). */
    displayNameKey?: string;
    descriptionKey: string;
    logoSrc: string;
    bannerBgColor: string;
}

/**
 * Vendor-Metadata for the FE: drives card rendering and connect-modal selection.
 * One entry per supported vendor.
 */
export interface VendorDefinition {
    vendor: IntegrationVendor;
    displayNameKey: string; // i18n key
    descriptionKey: string; // i18n key
    logoSrc: string;
    bannerBgColor: string;
    /**
     * Where this vendor's connection is set up + lives:
     *   - 'per-org':     one connection per organization (e.g. D365 BC). Managed in the
     *                    Org Settings. On a Company page the card only links the company
     *                    to an existing org connection (or shows a "manage in Org Settings"
     *                    redirect when nothing is set up yet).
     *   - 'per-company': connection is bound to a single company (e.g. Bexio). Managed
     *                    directly on the Company page.
     *
     * Drives which info-modal the universal Info button opens in each context.
     */
    scope: 'per-org' | 'per-company';
    mode: ConnectionMode;
    /**
     * Identifier used by the IntegrationsPage to pick the right connect-modal component.
     * Each vendor has its own modal (BC = MS OAuth flow, Abacus = credentials form, etc).
     */
    connectModalType: 'd365bc' | 'abacus' | 'topal' | 'proffix' | 'accounto' | 'credentials-form' | 'bexio' | 'sharepoint';
    setupDocsUrlKey?: string;
    setupFields?: VendorSetupField[];
    /**
     * Does this vendor's API support enumeration of external companies
     * (i.e. can the auto matcher pull a candidate list at all)? D365 BC has
     * `/external-companies`; Abacus has no equivalent — Mandanten IDs are
     * user typed per mapping. When false, the post Connect orchestration
     * skips `autoMatchTrigger` and the user lands in an empty manage modal
     * detail view ready to add mappings manually.
     */
    supportsAutoMatch?: boolean;
}
